import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { ArrowLeft, Brain, ClockClockwise, Lightning, Sparkle, ShareNetwork, Copy, ListNumbers } from "@phosphor-icons/react";
import AspirantDossierCard from "../components/AspirantDossierCard";

export default function UserProfile() {
  const { userId } = useParams();
  const { data, loading, reload } = useFetch(() => api.get(`/users/${userId}`).then((r) => r.data), [userId]);
  const { data: tl, reload: reloadTl } = useFetch(() => api.get(`/intelligence/user/${userId}/timeline`).then((r) => r.data), [userId]);
  const { data: dynLog } = useFetch(() => api.get(`/founder/dynamic-sequence/lead/${userId}`).then((r) => r.data), [userId]);
  const [analyzing, setAnalyzing] = useState(false);
  const [scoringAmb, setScoringAmb] = useState(false);
  const [realigning, setRealigning] = useState(false);
  const [originDraft, setOriginDraft] = useState("");
  const [savingOrigin, setSavingOrigin] = useState(false);

  const saveOrigin = async () => {
    setSavingOrigin(true);
    try {
      await api.patch(`/users/${userId}`, { lead_origin: originDraft.trim() });
      toast.success("Origen del lead guardado");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSavingOrigin(false); }
  };

  useEffect(() => {
    if (data?.user) setOriginDraft(data.user.lead_origin || "");
  }, [data?.user?.lead_origin]);

  const realignPipeline = async (targetSlug) => {
    const currentSlug = data?.user?.pipeline_slug || "—";
    const msg = `¿Reclasificar a este lead del pipeline "${currentSlug}" → "${targetSlug}"?\n\n` +
      `Esto desactiva sus memberships actuales y lo coloca en el stage inicial del pipeline destino. ` +
      `Útil cuando el lead fue clasificado mal por el triage automático.`;
    if (!window.confirm(msg)) return;
    setRealigning(true);
    try {
      const { data: res } = await api.post(`/users/${userId}/realign-pipeline`, { pipeline_slug: targetSlug });
      toast.success(`Lead reclasificado: ${res.from_pipeline || "—"} → ${res.to_pipeline} (stage ${res.new_stage_key})`);
      reload(); reloadTl();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al reclasificar");
    } finally { setRealigning(false); }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/intelligence/analyze/${userId}`, {});
      const actions = (data.automated_actions || []).length;
      toast.success(`Análisis IA listo · ${actions} acciones automatizadas`);
      reload(); reloadTl();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setAnalyzing(false); }
  };

  const runAmbassadorScoring = async () => {
    setScoringAmb(true);
    try {
      const { data: res } = await api.post(`/ambassador/score/${userId}`, {});
      const score = res?.score ?? 0;
      const stage = res?.stage || "—";
      toast.success(`Score ambassador: ${score}/100 → ${stage}`);
      reload(); reloadTl();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al evaluar");
    } finally { setScoringAmb(false); }
  };

  const reEngage = async (role) => {
    const label = role === "founder" ? "Founder" : "Ambassador";
    if (!confirm(`¿Reactivar este lead como ${label}? Se limpiarán las etiquetas de rechazo y se lo colocará en la etapa inicial.`)) return;
    try {
      const { data: res } = await api.post(`/users/${userId}/re-engage`, { role, reason: "Re-engaged desde perfil" });
      toast.success(`Re-engaged como ${label} en stage ${res.stage_key} · ${res.cancelled_jobs} jobs cancelados`);
      reload(); reloadTl();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al re-engagear");
    }
  };

  if (loading) return <div className="p-10 text-carbon-500">Cargando...</div>;
  if (!data) return <div className="p-10 text-signal">No se encontró el usuario.</div>;

  const u = data.user;
  const isOrigin = u.user_side === "origin_side";
  const originLine = [u.origin_country || u.country_origin, u.origin_municipality || u.municipality_origin].filter(Boolean).join(" / ");
  const currentLine = [u.current_country, u.current_city || u.current_municipality || u.usa_city].filter(Boolean).join(" / ");
  const ambassadorMembership = (data.memberships || []).find((m) => m.pipeline_slug === "ambassador" && m.active);
  const ambassadorScorableStages = new Set([
    "applicant", "priority_high", "priority_medium", "future_base", "filtered_out",
  ]);
  const canScoreAmbassador = ambassadorMembership && ambassadorScorableStages.has(ambassadorMembership.current_stage_key);

  return (
    <>
      <PageHeader
        label={isOrigin ? "Perfil unificado · ORIGIN-SIDE" : (u.user_side === "both" ? "Perfil unificado · BOTH" : "Perfil unificado · MIGRANT-SIDE")}
        title={u.name}
        description={`${u.public_id} · Origen: ${originLine || "—"} · Vive en: ${currentLine || "—"}`}
      >
        <Link to="/users" className="text-sm text-carbon-700 hover:bg-carbon-100 px-3 py-2 rounded flex items-center gap-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        <button onClick={runAnalysis} disabled={analyzing} data-testid="run-ai-analysis-btn" className="bg-terracotta text-white text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
          <Sparkle size={14} /> {analyzing ? "Analizando..." : "Analizar con IA"}
        </button>
        {canScoreAmbassador && (
          <button
            onClick={runAmbassadorScoring}
            disabled={scoringAmb}
            data-testid="run-ambassador-scoring-btn"
            className="bg-forest text-white text-sm px-3 py-2 rounded flex items-center gap-2 hover:opacity-90 disabled:opacity-60"
            title="Pide al motor IA reevaluar al postulante a Embajador (0-100) y moverlo al bucket correcto. Útil si ya hubo más conversación tras el primer scoring."
          >
            <Brain size={14} /> {scoringAmb ? "Evaluando..." : "Reevaluar Embajador"}
          </button>
        )}
        {/* Realinear pipeline · operador override cuando el routing clasificó mal */}
        {u.pipeline_slug !== "founder" && (
          <button
            onClick={() => realignPipeline("founder")}
            disabled={realigning}
            data-testid="realign-to-founder-btn"
            className="bg-terracotta/10 border border-terracotta text-terracotta text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-terracotta/20 disabled:opacity-60"
            title="Mueve este lead al pipeline Founder. Desactiva memberships actuales y lo coloca en new_lead."
          >
            → Founder
          </button>
        )}
        {u.pipeline_slug !== "ambassador" && (
          <button
            onClick={() => realignPipeline("ambassador")}
            disabled={realigning}
            data-testid="realign-to-ambassador-btn"
            className="bg-forest/10 border border-forest text-forest text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-forest/20 disabled:opacity-60"
            title="Mueve este lead al pipeline Ambassador. Desactiva memberships actuales y lo coloca en applicant."
          >
            → Ambassador
          </button>
        )}
        {/* Re-engage · útil cuando el lead dijo NO antes y ahora quiere volver */}
        {((u.tags || []).some((t) => ["not_interested", "filtered_out", "interview_rejected", "dormant", "founder_cold", "rejected"].includes(t)) ||
          (u.states || []).some((s) => ["not_interested", "filtered_out", "interview_rejected", "dormant", "founder_cold"].includes(s))) && (
          <div className="flex items-center gap-1 ml-2 border-l border-carbon-200 pl-2">
            <span className="text-[10px] text-carbon-500 uppercase tracking-wider mr-1">Re-engage</span>
            <button onClick={() => reEngage("founder")} data-testid="re-engage-founder-btn"
              className="bg-terracotta text-white text-xs px-2 py-1.5 rounded hover:bg-terracotta-hover">
              ↻ Founder
            </button>
            <button onClick={() => reEngage("ambassador")} data-testid="re-engage-ambassador-btn"
              className="bg-forest text-white text-xs px-2 py-1.5 rounded hover:opacity-90">
              ↻ Ambassador
            </button>
          </div>
        )}
      </PageHeader>

      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left column: identity + AI memory */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="p-5">
              <div className="section-label mb-2">Identidad</div>
              <div className="mb-3">
                <Badge tone={isOrigin ? "forest" : (u.user_side === "both" ? "warning" : "terracotta")}>
                  {isOrigin ? "origin-side" : (u.user_side === "both" ? "both sides" : "migrant-side")}
                </Badge>
                <Badge tone="neutral">{u.residence_type || "—"}</Badge>
              </div>
              <div className="text-xs text-carbon-500 mb-1">WhatsApp</div>
              <div className="font-mono text-sm mb-3">{u.whatsapp || "—"}</div>
              <div className="text-xs text-carbon-500 mb-1">Origen del lead</div>
              <div className="flex items-center gap-1 mb-3">
                <input value={originDraft} onChange={(e) => setOriginDraft(e.target.value)}
                  placeholder="ej: webinar"
                  data-testid="lead-origin-input"
                  className="flex-1 text-sm bg-page border border-carbon-200 rounded px-2 py-1.5 font-mono" />
                <button onClick={saveOrigin} disabled={savingOrigin || originDraft === (u.lead_origin || "")}
                  data-testid="lead-origin-save-btn"
                  className="text-xs px-2 py-1.5 bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-40">
                  {savingOrigin ? "..." : "Guardar"}
                </button>
              </div>
              <div className="text-xs text-carbon-500 mb-1">Origen</div>
              <div className="text-sm mb-3">
                {u.origin_country || u.country_origin || "—"}
                {u.origin_department && <span className="text-carbon-500"> · {u.origin_department}</span>}
                <br />
                <span className="text-xs text-carbon-500">
                  {u.origin_municipality || u.municipality_origin}
                  {u.origin_locality && ` · ${u.origin_locality}`}
                </span>
              </div>
              <div className="text-xs text-carbon-500 mb-1">Residencia actual</div>
              <div className="text-sm mb-3">
                {u.current_country || "—"}
                <br />
                <span className="text-xs text-carbon-500">{u.current_city || u.current_municipality || u.usa_city || "—"}</span>
              </div>
              {u.territorial_availability && (
                <>
                  <div className="text-xs text-carbon-500 mb-1">Disponibilidad territorial</div>
                  <div className="text-sm mb-3 font-mono">{u.territorial_availability}</div>
                </>
              )}
              <div className="text-xs text-carbon-500 mb-1">Engagement</div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-2 bg-carbon-100 rounded">
                  <div className="h-full bg-terracotta rounded" style={{ width: `${(u.engagement_score || 0) * 100}%` }} />
                </div>
                <span className="font-mono text-xs">{(u.engagement_score || 0).toFixed(2)}</span>
              </div>
              <div className="text-xs text-carbon-500 mb-1">Liderazgo</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-carbon-100 rounded">
                  <div className="h-full bg-forest rounded" style={{ width: `${(u.leadership_score || 0) * 100}%` }} />
                </div>
                <span className="font-mono text-xs">{(u.leadership_score || 0).toFixed(2)}</span>
              </div>
            </Card>

            <Card className="p-5">
              <div className="section-label mb-2">Roles</div>
              <div className="flex flex-wrap gap-1 mb-4">
                {(u.roles || []).map((r) => <Badge key={r} tone="terracotta">{r}</Badge>)}
                {(!u.roles || u.roles.length === 0) && <span className="text-xs text-carbon-400">Sin roles</span>}
              </div>
              <div className="section-label mb-2">Estados</div>
              <div className="flex flex-wrap gap-1 mb-4">
                {(u.states || []).map((s) => <Badge key={s} tone="forest">{s}</Badge>)}
              </div>
              <div className="section-label mb-2">Tags</div>
              <div className="flex flex-wrap gap-1">
                {(u.tags || []).map((t) => <Badge key={t}>{t}</Badge>)}
                {(!u.tags || u.tags.length === 0) && <span className="text-xs text-carbon-400">Sin tags</span>}
              </div>
            </Card>

            <Card className="p-5" testid="referrals-card">
              <div className="flex items-center gap-2 mb-2">
                <ShareNetwork size={16} className="text-forest" />
                <span className="section-label">Comunidad · Referidos</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-page rounded p-2 border border-carbon-200">
                  <div className="text-[10px] text-carbon-500 uppercase tracking-wide">Trajo</div>
                  <div className="font-heading text-2xl text-forest" data-testid="referrals-count">{u.referrals_count || 0}</div>
                  <div className="text-[10px] text-carbon-500">{u.referrals_count === 1 ? "lead" : "leads"}</div>
                </div>
                <div className="bg-page rounded p-2 border border-carbon-200">
                  <div className="text-[10px] text-carbon-500 uppercase tracking-wide">Referido por</div>
                  {data.referred_by ? (
                    <Link to={`/users/${data.referred_by.id}`} className="text-sm font-medium hover:text-terracotta block truncate" data-testid="referred-by-link">
                      {data.referred_by.name}
                    </Link>
                  ) : (
                    <div className="text-xs text-carbon-400 mt-2">Orgánico</div>
                  )}
                  {data.referred_by && <div className="text-[10px] text-carbon-500 font-mono">{data.referred_by.public_id}</div>}
                </div>
              </div>
              {data.referral_link ? (
                <>
                  <div className="text-xs text-carbon-500 mb-1">Link de referidos personal</div>
                  <div className="flex items-center gap-2 mb-3">
                    <input
                      readOnly
                      value={data.referral_link}
                      data-testid="referral-link-input"
                      className="flex-1 text-[11px] font-mono bg-page border border-carbon-200 rounded px-2 py-1.5 truncate"
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(data.referral_link); toast.success("Link copiado"); }}
                      data-testid="copy-referral-link"
                      className="p-1.5 text-carbon-500 hover:text-forest hover:bg-forest/5 rounded border border-carbon-200"
                      title="Copiar"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-xs text-carbon-400 mb-3">
                  Configurá el número de comunidad en <Link to="/settings" className="text-terracotta hover:underline">/settings</Link> para activar el link.
                </div>
              )}
              {(data.referrals || []).length > 0 && (
                <>
                  <div className="text-xs text-carbon-500 mb-1">Leads traídos</div>
                  <div className="space-y-1">
                    {(data.referrals || []).slice(0, 5).map((r) => (
                      <Link key={r.id} to={`/users/${r.id}`} className="block text-xs bg-page p-2 rounded border border-carbon-200 hover:border-forest/40">
                        <div className="font-medium truncate">{r.name}</div>
                        <div className="text-[10px] text-carbon-500 font-mono">{r.public_id}</div>
                      </Link>
                    ))}
                    {(data.referrals || []).length > 5 && (
                      <div className="text-[10px] text-carbon-500 text-center pt-1">+{data.referrals.length - 5} más</div>
                    )}
                  </div>
                </>
              )}
            </Card>

            <Card className="p-5" testid="ai-memory-card">
              <div className="flex items-center gap-2 mb-2">
                <Brain size={16} className="text-terracotta" />
                <span className="section-label">AI Memory</span>
              </div>
              <div className="text-xs text-carbon-500 mb-1">Resúmenes</div>
              <div className="space-y-2 mb-3">
                {(data.ai_memory?.ai_summaries || []).slice(-3).map((s, i) => (
                  <div key={i} className="text-xs bg-page p-2 rounded border border-carbon-200">{s.summary}</div>
                ))}
                {(!data.ai_memory?.ai_summaries || data.ai_memory.ai_summaries.length === 0) && (
                  <div className="text-xs text-carbon-400">Sin resúmenes</div>
                )}
              </div>
              <div className="text-xs text-carbon-500 mb-1">Intereses detectados</div>
              <div className="flex flex-wrap gap-1">
                {(data.ai_memory?.detected_interests || []).map((i, k) => <Badge key={k}>{i}</Badge>)}
                {(!data.ai_memory?.detected_interests || data.ai_memory.detected_interests.length === 0) && (
                  <span className="text-xs text-carbon-400">Sin señales</span>
                )}
              </div>
            </Card>
          </div>

          {/* Right column: timeline + decision logs + pipelines */}
          <div className="lg:col-span-3 space-y-4">
            {(u.journey_version === "v2" || u.pipeline_slug === "aspirantes_v2") ? (
              <AspirantDossierCard userId={userId} />
            ) : (
              <Card className="p-5" data-testid="reactivate-v2-card">
                <div className="section-label mb-1">Journey V2 · Aspirante a Embajador</div>
                <p className="text-sm text-carbon-500 mb-3">
                  Este lead pertenece al journey anterior. Puedes reactivarlo en el nuevo journey V2 preservando su historial; los estados antiguos se guardan como contexto legacy (no como evidencia).
                </p>
                <button
                  data-testid="reactivate-v2-btn"
                  onClick={async () => {
                    if (!window.confirm("¿Reactivar este lead en el Journey V2? Se preserva el historial.")) return;
                    try {
                      await api.post(`/aspirants/${userId}/reactivate-v2`, {});
                      toast.success("Lead reactivado en Journey V2");
                      window.location.reload();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
                  }}
                  className="bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover"
                >
                  Reactivar en Journey V2
                </button>
              </Card>
            )}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lightning size={16} className="text-terracotta" />
                <div className="section-label">Pipelines del usuario</div>
              </div>
              <p className="text-xs text-carbon-500 mb-3">
                Un usuario puede pertenecer a varios pipelines a la vez (founder, ambassador, etc.).
                El <strong>score del pipeline</strong> es interno a esa cinta — p. ej. el Ambassador Pipeline
                usa 0–100 para priorizar reclutamiento; el Founder Pipeline no lo usa (queda en 0).
                Los scores globales de comunidad (engagement, liderazgo) están en la columna izquierda.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(data.memberships || []).map((m) => {
                  const isAmbassador = m.pipeline_slug === "ambassador";
                  return (
                    <div key={m.id} className="border border-carbon-200 rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">{m.pipeline_name || m.pipeline_slug || "Pipeline"}</span>
                        <Badge tone={isAmbassador ? "forest" : "terracotta"}>
                          {m.pipeline_slug || "—"}
                        </Badge>
                      </div>
                      <div className="text-xs text-carbon-500">Stage actual</div>
                      <div className="text-sm mb-2">
                        {m.current_stage_name || m.current_stage_key || <span className="text-carbon-400 font-mono text-xs">{(m.current_stage_id || "—").slice(0, 8)}…</span>}
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-carbon-100">
                        <span className="text-xs text-carbon-500">
                          Score del pipeline {isAmbassador ? "(0–100)" : "(no usado)"}
                        </span>
                        <span className="font-mono text-xs text-terracotta">
                          {isAmbassador
                            ? (u.ambassador_score || m.score || 0)
                            : (m.score || 0).toFixed(2)}
                        </span>
                      </div>
                      {isAmbassador && (u.ambassador_score || 0) > 0 && (
                        <div className="mt-2 pt-2 border-t border-carbon-100 space-y-1">
                          {u.primary_type && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-carbon-500">Tipo primario</span>
                              <Badge tone="forest">{u.primary_type}</Badge>
                            </div>
                          )}
                          {u.secondary_type && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-carbon-500">Tipo secundario</span>
                              <Badge tone="neutral">{u.secondary_type}</Badge>
                            </div>
                          )}
                          {(u.interview_red_flags || []).length > 0 && (
                            <div className="text-[10px] text-signal mt-1">
                              ⚠ Red flags: {(u.interview_red_flags || []).join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                      {!m.active && <div className="mt-2"><Badge tone="neutral">inactivo</Badge></div>}
                    </div>
                  );
                })}
                {(data.memberships || []).length === 0 && (
                  <div className="text-sm text-carbon-400 col-span-full">No pertenece a ningún pipeline</div>
                )}
              </div>
            </Card>

            <Card className="p-5" testid="ai-decision-logs">
              <div className="flex items-center gap-2 mb-3">
                <Brain size={16} className="text-forest" />
                <div className="section-label">AI Decision Logs</div>
                <Badge tone="forest">Auditabilidad</Badge>
              </div>
              <div className="space-y-2 max-h-[280px] overflow-y-auto scrollbar-thin">
                {(data.ai_decision_logs || []).map((l) => (
                  <div key={l.id} className="border border-carbon-200 rounded p-3">
                    <div className="flex items-center justify-between mb-1">
                      <Badge tone="terracotta">{l.decision_type}</Badge>
                      <span className="font-mono text-[10px] text-carbon-500">{new Date(l.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-carbon-900">{l.explanation_summary}</div>
                    <div className="text-xs text-carbon-500 mt-1">
                      Modelo: <span className="font-mono">{l.provider_used}/{l.model_used}</span> · Confianza: <span className="font-mono">{l.confidence_score?.toFixed?.(2)}</span>
                    </div>
                  </div>
                ))}
                {(data.ai_decision_logs || []).length === 0 && (
                  <div className="text-sm text-carbon-400">Sin decisiones registradas</div>
                )}
              </div>
            </Card>

            <Card className="p-5" testid="ai-intelligence-timeline">
              <div className="flex items-center gap-2 mb-3">
                <Brain size={16} className="text-terracotta" />
                <div className="section-label">AI Intelligence Timeline</div>
                <Badge tone="terracotta">{tl?.signals?.length || 0} señales</Badge>
              </div>
              {tl?.user && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  <ScoreBox label="Leadership" value={tl.user.leadership_score} testid="score-leadership" />
                  <ScoreBox label="Engagement" value={tl.user.engagement_score} extra={tl.user.engagement_level} testid="score-engagement" />
                  <ScoreBox label="Emotional" value={tl.user.emotional_affinity_score} testid="score-emotional" />
                  <ScoreBox label="Moderation" value={tl.user.moderation_risk_score} signal testid="score-moderation" />
                </div>
              )}
              <div className="space-y-2 max-h-[360px] overflow-y-auto scrollbar-thin">
                {(tl?.signals || []).map((s) => (
                  <div key={s.id} className="border border-carbon-200 rounded p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge tone="terracotta">{s.module}</Badge>
                      <span className="font-mono text-xs">score {s.score?.toFixed?.(2)}</span>
                      <span className="font-mono text-xs text-carbon-500">conf {s.confidence?.toFixed?.(2)}</span>
                      <span className="font-mono text-[10px] text-carbon-500 ml-auto">{new Date(s.created_at).toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-carbon-900">{s.summary}</div>
                    <div className="text-xs text-carbon-500 mt-1">{s.reasoning}</div>
                    {(s.signals || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {s.signals.map((sg, i) => <Badge key={i}>{sg}</Badge>)}
                      </div>
                    )}
                  </div>
                ))}
                {(!tl?.signals || tl.signals.length === 0) && (
                  <div className="text-sm text-carbon-400">Sin análisis IA aún. Pulsa "Analizar con IA" arriba.</div>
                )}
              </div>
              {(tl?.actions || []).length > 0 && (
                <div className="mt-4 border-t border-carbon-200 pt-3">
                  <div className="section-label mb-2">Acciones automatizadas</div>
                  <div className="space-y-1 max-h-[150px] overflow-y-auto scrollbar-thin">
                    {tl.actions.slice(0, 10).map((a) => (
                      <div key={a.id} className="text-xs flex items-center gap-2">
                        <Badge tone={a.applied ? "forest" : "warning"}>{a.applied ? "OK" : "rec"}</Badge>
                        <span className="font-mono">{a.module}/{a.action}</span>
                        <span className="text-carbon-500">→ {a.target}</span>
                        <span className="font-mono text-[10px] text-carbon-500 ml-auto">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-5" testid="dynamic-sequence-log">
              <div className="flex items-center gap-2 mb-3">
                <ListNumbers size={16} className="text-terracotta" />
                <div className="section-label">Bitácora de Dinámicas</div>
                {dynLog?.status && (
                  <Badge tone={dynLog.status === "completed" ? "forest" : dynLog.status === "in_progress" ? "terracotta" : "neutral"}>
                    {dynLog.status === "not_enrolled" ? "sin enrolar" : dynLog.status === "in_progress" ? "en progreso" : dynLog.status}
                  </Badge>
                )}
                {dynLog?.status === "in_progress" && (
                  <span className="text-xs text-carbon-500 ml-auto">Próxima dinámica: #{dynLog.next_position}</span>
                )}
              </div>
              <div className="space-y-2 max-h-[360px] overflow-y-auto scrollbar-thin">
                {(dynLog?.responses || []).map((r, i) => (
                  <div key={i} className="border border-carbon-200 rounded p-3" data-testid={`dynamic-response-${r.position}`}>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="w-6 h-6 rounded-full bg-terracotta-light text-terracotta text-xs font-bold flex items-center justify-center">{r.position}</span>
                      <span className="font-mono text-[10px] text-carbon-500 ml-auto">{r.sent_at ? new Date(r.sent_at).toLocaleString() : ""}</span>
                    </div>
                    {r.question && <div className="text-xs text-carbon-500 mb-1">Pregunta: <span className="text-carbon-900">{r.question}</span></div>}
                    {r.response ? (
                      <div className="bg-forest-light/40 border border-forest/20 rounded p-2 mt-1">
                        <div className="text-[10px] uppercase tracking-wider text-forest mb-0.5">Respuesta del lead</div>
                        <div className="text-sm text-carbon-900">{r.response}</div>
                        {r.response_at && <div className="text-[10px] font-mono text-carbon-500 mt-1">{new Date(r.response_at).toLocaleString()}</div>}
                      </div>
                    ) : (
                      <div className="text-xs text-carbon-400 italic mt-1">Enviada · sin respuesta aún</div>
                    )}
                  </div>
                ))}
                {(!dynLog?.responses || dynLog.responses.length === 0) && (
                  <div className="text-sm text-carbon-400">
                    {dynLog?.status === "not_enrolled"
                      ? "Este lead aún no está enrolado en la secuencia de dinámicas (solo founders en stage confirmed/active/core/leader_potential)."
                      : "Sin dinámicas enviadas todavía."}
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <ClockClockwise size={16} />
                <div className="section-label">Timeline · Eventos & mensajes</div>
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto scrollbar-thin">
                {data.events?.map((e) => (
                  <div key={e.id} className="flex items-start gap-3 text-sm">
                    <div className="w-1.5 h-1.5 mt-2 bg-terracotta rounded-full flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge>{e.event_type}</Badge>
                        <span className="text-[10px] font-mono text-carbon-500">{new Date(e.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {data.recent_messages?.map((m) => (
                  <div key={m.id} className="flex items-start gap-3 text-sm">
                    <div className={`w-1.5 h-1.5 mt-2 ${m.direction === "inbound" ? "bg-forest" : "bg-terracotta"} rounded-full flex-shrink-0`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <Badge tone={m.direction === "inbound" ? "forest" : "terracotta"}>{m.direction}</Badge>
                        {m.ai_origin && <Badge tone="warning">IA</Badge>}
                        <span className="text-[10px] font-mono text-carbon-500">{new Date(m.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-xs text-carbon-700">{m.body}</div>
                    </div>
                  </div>
                ))}
                {data.events?.length === 0 && data.recent_messages?.length === 0 && (
                  <div className="text-sm text-carbon-400">Sin eventos aún</div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function ScoreBox({ label, value, extra, signal = false, testid }) {
  const v = Number(value || 0);
  const color = signal
    ? (v >= 0.6 ? "text-signal" : v >= 0.3 ? "text-warning" : "text-forest")
    : (v >= 0.7 ? "text-forest" : v >= 0.4 ? "text-terracotta" : "text-carbon-500");
  return (
    <div data-testid={testid} className="border border-carbon-200 rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-carbon-500">{label}</div>
      <div className={`font-heading text-xl font-light tracking-tighter ${color}`}>{v.toFixed(2)}</div>
      {extra && <div className="text-[10px] font-mono text-carbon-500">{extra}</div>}
    </div>
  );
}
